/**
 * Pure mapping from the bespoke wizard's answers to the concierge engine's
 * form fields. Extracted from the (client) wizard component so the
 * answer→engine contract is unit-testable in isolation — this is the seam
 * that decides what the engine actually receives from a visitor's clicks.
 */

import { MEAL_WINDOWS, SHOPPING_INTEREST } from "./constants";
import { minutesToTime, timeToMinutes } from "./distance";
import type { MealType } from "./types";

// Wizard "duration" → engine visit window + which meals to slot.
export const DURATION_TO_WINDOW: Record<string, string> = {
  "1h": "morning",
  "2-3h": "afternoon",
  half: "fullDay",
  full: "fullDay",
};

export const DURATION_TO_MEALS: Record<string, string[]> = {
  "1h": ["coffee"],
  "2-3h": ["lunch"],
  half: ["lunch", "snack"],
  full: ["coffee", "lunch", "dinner"],
};

const HH_MM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

// Wizard "duration" → how long the day runs from a chosen arrival time.
export const DURATION_MINUTES: Record<string, number> = {
  "1h": 60,
  "2-3h": 180,
  half: 300,
  full: 12 * 60,
};

/** Latest a planned day runs to — matches the evening window's close. */
export const LATEST_DEPARTURE_MINUTES = 22 * 60;

const EVENING_START_MINUTES = 17 * 60;

export interface DurationOption {
  id: "1h" | "2-3h" | "half" | "full";
  label: string;
  sub: string;
}

const DEFAULT_DURATION_OPTIONS: DurationOption[] = [
  { id: "1h", label: "An hour", sub: "A taster" },
  { id: "2-3h", label: "2 – 3 hours", sub: "A proper visit" },
  { id: "half", label: "Half a day", sub: "4 – 6 hours" },
  { id: "full", label: "A full day", sub: "Morning to sundown" },
];

function restOfDayOption(start: number): DurationOption {
  if (start < 12 * 60) return DEFAULT_DURATION_OPTIONS[3]!;
  if (start < EVENING_START_MINUTES)
    return { id: "full", label: "Into the evening", sub: "Dinner included" };
  return {
    id: "full",
    label: "The whole evening",
    sub: "Dinner, drinks & late spots",
  };
}

/**
 * The "How long do you have?" choices for an arrival time: the set lengths
 * that end before the latest departure, then the rest of the day — a full day
 * from the morning, into the evening from the afternoon, or the evening.
 */
export function durationOptionsFor(
  arrivalTime?: string | null,
): DurationOption[] {
  if (!arrivalTime || !HH_MM_RE.test(arrivalTime))
    return DEFAULT_DURATION_OPTIONS;
  const start = timeToMinutes(arrivalTime);
  const remaining = LATEST_DEPARTURE_MINUTES - start;
  const fixed = DEFAULT_DURATION_OPTIONS.filter(
    (o) => o.id !== "full" && DURATION_MINUTES[o.id]! < remaining,
  );
  return [...fixed, restOfDayOption(start)];
}

const MEALS_BY_TIME: MealType[] = [
  "coffee",
  "lunch",
  "snack",
  "drinks",
  "dinner",
];

// Wizard "group" → party composition.
export const GROUP_TO_PARTY: Record<
  string,
  { adults: number; children: number }
> = {
  solo: { adults: 1, children: 0 },
  couple: { adults: 2, children: 0 },
  family: { adults: 2, children: 2 },
  friends: { adults: 4, children: 0 },
};

// Wizard interests → interest slugs the engine scores and gates against.
// `shop` maps to the shopping-intent signal that admits stores into the
// plan; the rest are placeTag slugs.
export const INTEREST_TO_TAGS: Record<string, string[]> = {
  art: ["cultural"],
  food: [],
  shop: [SHOPPING_INTEREST],
  nature: ["outdoor"],
  history: ["heritage", "cultural"],
  family: ["family", "kid-friendly"],
  sundowner: ["photogenic"],
};

const DEFAULT_DURATION = "2-3h";
const DEFAULT_GROUP = "couple";
const DEFAULT_PARTY = { adults: 2, children: 0 };

export interface WizardAnswers {
  duration: string | null;
  group: string | null;
  interests: string[];
  /** ISO YYYY-MM-DD. Optional — the engine defaults to today when absent. */
  date?: string | null;
  /** "easy" | "balanced" | "packed" — nudges the target stop count. */
  pace?: string | null;
  /** "HH:MM" arrival. Optional — the duration's default window applies when absent. */
  arrivalTime?: string | null;
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export interface ConciergeFormFields {
  visitWindow: string;
  adults: number;
  children: number;
  mealPreferences: string[];
  interests: string[];
  startTime?: string;
  endTime?: string;
}

/**
 * The [start, end] of the day for an arrival time, in "HH:MM". The end is
 * the arrival plus the chosen duration, capped at the latest departure.
 */
export function arrivalRange(
  arrivalTime: string,
  duration: string,
): { startTime: string; endTime: string } {
  const start = timeToMinutes(arrivalTime);
  const length = DURATION_MINUTES[duration] ?? DURATION_MINUTES["2-3h"]!;
  const end = Math.max(
    Math.min(start + length, LATEST_DEPARTURE_MINUTES),
    Math.min(start + 60, 24 * 60 - 1),
  );
  return { startTime: arrivalTime, endTime: minutesToTime(end) };
}

/**
 * Meals for a day that starts and ends at set times: the duration's usual
 * meals that fall inside the day, or failing that the first meal that does.
 */
function mealsForRange(
  duration: string,
  startTime: string,
  endTime: string,
): MealType[] {
  const start = timeToMinutes(startTime);
  const end = timeToMinutes(endTime);
  const inRange = MEALS_BY_TIME.filter(
    (m) => MEAL_WINDOWS[m].start < end && MEAL_WINDOWS[m].end > start,
  );
  const usual =
    duration === "full" && start >= EVENING_START_MINUTES
      ? ["drinks", "dinner"]
      : (DURATION_TO_MEALS[duration] ?? []);
  const preferred = usual.filter((m) =>
    inRange.includes(m as MealType),
  ) as MealType[];
  if (preferred.length > 0) return preferred;
  return inRange.slice(0, 1);
}

/**
 * The engine window a set day behaves like, which sizes the target number of
 * stops — so an evening visit isn't padded out like a full day.
 */
function windowForRange(startTime: string, endTime: string): string {
  const start = timeToMinutes(startTime);
  const length = timeToMinutes(endTime) - start;
  if (start >= EVENING_START_MINUTES) return "evening";
  if (length <= 210) return start < 12 * 60 + 30 ? "morning" : "afternoon";
  return "fullDay";
}

/**
 * Resolve wizard answers to the engine's form field values, applying the
 * same defaults the wizard uses when a step is skipped.
 */
export function mapWizardAnswers(answers: WizardAnswers): ConciergeFormFields {
  const duration = answers.duration ?? DEFAULT_DURATION;
  const party = GROUP_TO_PARTY[answers.group ?? DEFAULT_GROUP] ?? DEFAULT_PARTY;

  // De-dupe tags across interests (e.g. art + history both add "cultural").
  const tags = new Set<string>();
  for (const interest of answers.interests) {
    for (const tag of INTEREST_TO_TAGS[interest] ?? []) tags.add(tag);
  }

  const fields: ConciergeFormFields = {
    visitWindow: DURATION_TO_WINDOW[duration] ?? "fullDay",
    adults: party.adults,
    children: party.children,
    mealPreferences: DURATION_TO_MEALS[duration] ?? [],
    interests: [...tags],
  };

  if (answers.arrivalTime && HH_MM_RE.test(answers.arrivalTime)) {
    const { startTime, endTime } = arrivalRange(answers.arrivalTime, duration);
    fields.startTime = startTime;
    fields.endTime = endTime;
    fields.visitWindow = windowForRange(startTime, endTime);
    fields.mealPreferences = mealsForRange(duration, startTime, endTime);
  }

  return fields;
}

/**
 * Build the FormData the server action consumes, from wizard answers.
 * Shared by the wizard component and the pipeline scenario tests so both
 * exercise the exact same answer→engine translation.
 */
export function buildConciergeFormData(answers: WizardAnswers): FormData {
  const fields = mapWizardAnswers(answers);
  const fd = new FormData();
  if (answers.date && ISO_DATE_RE.test(answers.date)) {
    fd.set("date", answers.date);
  }
  if (
    answers.pace === "easy" ||
    answers.pace === "balanced" ||
    answers.pace === "packed"
  ) {
    fd.set("pace", answers.pace);
  }
  fd.set("visitWindow", fields.visitWindow);
  if (fields.startTime && fields.endTime) {
    fd.set("startTime", fields.startTime);
    fd.set("endTime", fields.endTime);
  }
  fd.set("adults", String(fields.adults));
  fd.set("children", String(fields.children));
  for (const meal of fields.mealPreferences) fd.append("mealPreferences", meal);
  for (const tag of fields.interests) fd.append("interests", tag);
  return fd;
}
